import React, { useState, useRef, useEffect } from 'react';
import type { Course } from 'Redux/courseCompare/types';
import NextImage from 'next/image';

import Feature from '../Feature';
import {
    PersonChartGrowth,
    Clipboard,
    Ribbon,
    Newspaper,
    NotebookPerson,
    Alarm,
    Briefcase,
    Globe,
    FlagCapture,
    PuzzlePieceHand,
    Transfer,
    NotebookPen,
    PuzzleMine
} from '@solent-university/solent-icons';

import {
    Container,
    HeadingsColumn,
    DataColumns,
    Column,
    Heading,
    HeadingText,
    IconWrapper,
    Icon,
    CourseName,
    RemoveButton,
    RemoveIcon,
    CourseLevel,
    CourseImageWrapper,
    Cell,
    Button,
    RemoveTextButton
} from './Table.styled';

export interface Props {
    courses: Course[];
    close: () => void;
    clearCourses: () => void;
    removeCourse: (course: Course) => void;
}

const Table: React.FC<Props> = props => {
    const { courses, removeCourse, clearCourses, close } = props;
    const [tableScrollHeight, setTableScrollHeight] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const tableRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const dataColumnsRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const handleTableResize = () => {
        setTableScrollHeight(tableRef.current.scrollHeight);
    };

    const handleDataColumnsScroll = () => {
        setIsScrolling(dataColumnsRef.current.scrollLeft !== 0);
    };

    useEffect(() => {
        handleTableResize();

        const tableEl = tableRef.current;
        const dataColumnsEl = dataColumnsRef.current;
        tableEl.addEventListener('resize', handleTableResize);
        dataColumnsEl.addEventListener('scroll', handleDataColumnsScroll);

        return () => {
            tableEl.removeEventListener('resize', handleTableResize);
            dataColumnsEl.removeEventListener('scroll', handleDataColumnsScroll);
        };
    }, []);

    return (
        <Container role="table" ref={tableRef} $scrollHeight={tableScrollHeight}>
            <div>
                <HeadingsColumn role="row" $isScrolling={isScrolling}>
                    <Heading role="columnheader" $hidden>
                        Course name
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={PersonChartGrowth} />
                        </IconWrapper>
                        <HeadingText>Course level</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Clipboard} />
                        </IconWrapper>
                        <HeadingText>Entry requirements</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Ribbon} />
                        </IconWrapper>
                        <HeadingText>Professional accreditation</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Newspaper} />
                        </IconWrapper>
                        <HeadingText>Additional qualification</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={NotebookPerson} />
                        </IconWrapper>
                        <HeadingText>Professional memberships</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Alarm} />
                        </IconWrapper>
                        <HeadingText>Sandwich year</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Briefcase} />
                        </IconWrapper>
                        <HeadingText>Built in work experience placement</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Globe} />
                        </IconWrapper>
                        <HeadingText>Study abroad</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={FlagCapture} />
                        </IconWrapper>
                        <HeadingText>Placement year</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={PuzzlePieceHand} />
                        </IconWrapper>
                        <HeadingText>Foundation year available</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Transfer} />
                        </IconWrapper>
                        <HeadingText>Top-up option</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={NotebookPen} />
                        </IconWrapper>
                        <HeadingText>Further studies</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={PuzzleMine} />
                        </IconWrapper>
                        <HeadingText>Specialist facilities</HeadingText>
                    </Heading>
                </HeadingsColumn>
                <RemoveTextButton
                    onClick={() => {
                        clearCourses();
                        close();
                    }}
                    aria-label="Remove all courses from compare"
                    className="m-course-compare-remove-all"
                >
                    Remove all courses
                </RemoveTextButton>
            </div>
            <DataColumns $colCount={courses.length} ref={dataColumnsRef} role="rowgroup">
                {courses.map(course => (
                    <div key={course.id}>
                        <Column role="row">
                            <CourseName role="cell">
                                <CourseImageWrapper>
                                    <NextImage
                                        src={course.image.src}
                                        alt=""
                                        fill
                                        style={{ objectFit: 'cover' }}
                                    />
                                </CourseImageWrapper>
                                <RemoveButton
                                    onClick={() => {
                                        removeCourse(course);
                                        courses.length === 1 && close();
                                    }}
                                    aria-label="Remove course from comapare"
                                >
                                    <RemoveIcon />
                                </RemoveButton>
                                {course.name}
                            </CourseName>
                            <CourseLevel role="cell">{course.level}</CourseLevel>
                            <Cell role="cell">{course.entryRequirements}</Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.professionalMemberships}
                                    name="professional accreditation"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.additionalQualification}
                                    name="additional qualification"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.professionalMemberships}
                                    name="professional memberships"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.sandwichYear}
                                    name="sandwich year"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.workExperiencePlacement}
                                    name="built in work experience placement"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.studyAbroad}
                                    name="study abroad"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.placementYear}
                                    name="placement year"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.foundationYear}
                                    name="foundation year"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.topUpOption}
                                    name="top-up option"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.furtherStudies}
                                    name="further studies"
                                />
                            </Cell>
                            <Cell role="cell">
                                <Feature
                                    hasFeature={course.features.specialistFacilities}
                                    name="specialist facilities"
                                />
                            </Cell>
                        </Column>
                        <Button
                            aria-label={`Go to ${course.name} course page`}
                            href={course.link.href}
                            onClick={close}
                        >
                            View course
                        </Button>
                        <RemoveTextButton
                            onClick={() => {
                                removeCourse(course);
                                courses.length === 1 && close();
                            }}
                            className="m-course-compare-remove"
                            aria-label="Remove course from compare"
                        >
                            Remove course
                        </RemoveTextButton>
                    </div>
                ))}
            </DataColumns>
        </Container>
    );
};

export default Table;
